import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Row,
  Col,
  Form,
  Alert,
  Spinner,
  Modal,
  Table,
  InputGroup,
} from "react-bootstrap";
import {
  IconSearch,
  IconPackage,
  IconAlertTriangle,
  IconTrendingUp,
} from "@tabler/icons-react";
import Select from "react-select";
// import AsyncSelect from "react-select/async";
import { API_URL } from "utils/constants";
import { useWarehouse } from "../context/WarehouseContext";
import { useWorkshopSupplier } from "../context/WorkshopSupplierContext";
import { useInventory } from "../context/InventoryContext";
import { useCategory } from "../context/CategoryContext";
// import { getRequest } from "services/api";
import PrintJson from "utils/printJson";

const PurchaseOrderForm = ({
  initialData,
  onSuccess,
  onError,
  show,
  onHide,
}) => {
  // Helper function to format numbers with thousand separators
  const formatRupiah = (number) => {
    return new Intl.NumberFormat("id-ID").format(number);
  };

  // Format number for display (like MasterDataFormV2)
  const formatNumber = (value) => {
    const number = value.replace(/\D/g, "");
    return new Intl.NumberFormat("id-ID").format(Number(number));
  };

  // Unformat number for storage
  const unformatNumber = (value) => {
    return value.replace(/\./g, "");
  };

  // Get data from contexts
  const { warehouses } = useWarehouse();
  const { suppliers } = useWorkshopSupplier();
  const { inventory, Pagination } = useInventory();
  const { categories } = useCategory();

  // Form state
  const [formData, setFormData] = useState({
    warehouse_id: "",
    requester_id: "",
    supplier_id: "",
    note: "",
    items: [],
    subtotal: 0,
    tax_amount: 0,
    discount_amount: 0,
    total_amount: 0,
  });

  // Selected options for display
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedRequester, setSelectedRequester] = useState(null);
  console.log("selectedRequester", selectedRequester);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  // Table state
  // const [showLowStockOnly, setShowLowStockOnly] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 200;

  // Advanced filtering state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [allCategories, setAllCategories] = useState([]);
  console.log("allCategories", allCategories);

  // Load categories for filtering
  useEffect(() => {
    if (categories) {
      const categoryOptions = categories.map((category) => ({
        value: category.id,
        label: category.name,
      }));
      setAllCategories(categoryOptions);
    }
  }, [categories]);

  const isUpdateMode = initialData && initialData.id;

  // Auto-set requester from logged-in user
  useEffect(() => {
    if (!isUpdateMode) {
      try {
        const userData = localStorage.getItem("user");
        if (userData) {
          const user = JSON.parse(userData);
          if (user && user.id) {
            setFormData((prev) => ({ ...prev, requester_id: user.id }));
            setSelectedRequester({
              value: user.id,
              label: user.name || user.email || `User ${user.id}`,
            });
          }
        }
      } catch (error) {
        console.error("Error getting user from localStorage:", error);
      }
    }
  }, [isUpdateMode]);

  // Auto-select "Office" warehouse for create mode
  useEffect(() => {
    if (!isUpdateMode && warehouses && warehouses.length > 0) {
      const officeWarehouse = warehouses.find(
        (warehouse) => warehouse.name === "Office",
      );
      if (officeWarehouse) {
        setFormData((prev) => ({ ...prev, warehouse_id: officeWarehouse.id }));
        setSelectedWarehouse({
          value: officeWarehouse.id,
          label: officeWarehouse.name,
        });
      }
    }
  }, [isUpdateMode, warehouses]);

  // Initialize form data - populate with all inventory items
  useEffect(() => {
    if (inventory?.data && inventory.data.length > 0) {
      let items = [];

      if (initialData && initialData.items) {
        // For edit mode, map existing items and merge with all inventory
        const existingItemsMap = new Map();
        initialData.items.forEach((item) => {
          existingItemsMap.set(item.inventory_id, {
            quantity: item.quantity_ordered || item.quantity || 0,
            unit_price: parseFloat(item.unit_price) || 0,
            total_price: parseFloat(item.total_price) || 0,
          });
        });

        items = inventory.data.map((invItem) => ({
          inventory_id: invItem.id,
          quantity: existingItemsMap.get(invItem.id)?.quantity || 0,
          unit_price: existingItemsMap.get(invItem.id)?.unit_price || 0,
          total_price: existingItemsMap.get(invItem.id)?.total_price || 0,
        }));
      } else {
        // For create mode, initialize all items with zero quantity
        items = inventory.data.map((invItem) => ({
          inventory_id: invItem.id,
          quantity: 0,
          unit_price: 0,
          total_price: 0,
        }));
      }

      setFormData((prev) => ({
        ...prev,
        warehouse_id: initialData?.warehouse_id || prev.warehouse_id,
        requester_id: initialData?.requester_id || prev.requester_id,
        supplier_id: initialData?.supplier_id || prev.supplier_id,
        note: initialData?.note || prev.note,
        items: items,
        subtotal: initialData?.subtotal || 0,
        total_amount: initialData?.total_amount || 0,
      }));

      // Set selected options for display
      if (initialData?.warehouse) {
        setSelectedWarehouse({
          value: initialData.warehouse.id,
          label: initialData.warehouse.name,
        });
      }
      if (initialData?.supplier) {
        setSelectedSupplier({
          value: initialData.supplier.id,
          label: initialData.supplier.name,
        });
      }
    }
  }, [inventory, initialData]);

  // Prepare options from contexts
  const warehouseOptions = (warehouses || []).map((warehouse) => ({
    value: warehouse.id,
    label: warehouse.name,
  }));

  const supplierOptions = (suppliers || []).map((supplier) => ({
    value: supplier.id,
    label: supplier.name,
  }));

  // Filter and paginate items
  const getFilteredItems = () => {
    if (!inventory?.data) return [];

    let filtered = inventory.data;

    // Search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name?.toLowerCase().includes(searchLower) ||
          item.description?.toLowerCase().includes(searchLower),
      );
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(
        (item) => item.category_id === selectedCategory.value,
      );
    }

    // Status filter
    if (statusFilter === "low") {
      if (formData.warehouse_id) {
        filtered = filtered.filter((item) => {
          const warehouseStock = item.warehouse_stocks?.find(
            (ws) => ws.warehouse_id === formData.warehouse_id,
          );
          return (
            warehouseStock &&
            warehouseStock.stock <= warehouseStock.minimum_stock
          );
        });
      } else {
        filtered = filtered.filter((item) => {
          return item.warehouse_stocks?.some(
            (ws) => ws.stock <= ws.minimum_stock,
          );
        });
      }
    } else if (statusFilter === "out") {
      if (formData.warehouse_id) {
        filtered = filtered.filter((item) => {
          const warehouseStock = item.warehouse_stocks?.find(
            (ws) => ws.warehouse_id === formData.warehouse_id,
          );
          return warehouseStock && warehouseStock.stock === 0;
        });
      } else {
        filtered = filtered.filter((item) => {
          return item.warehouse_stocks?.every((ws) => ws.stock === 0);
        });
      }
    } else if (statusFilter === "good") {
      if (formData.warehouse_id) {
        filtered = filtered.filter((item) => {
          const warehouseStock = item.warehouse_stocks?.find(
            (ws) => ws.warehouse_id === formData.warehouse_id,
          );
          return (
            warehouseStock &&
            warehouseStock.stock > warehouseStock.minimum_stock
          );
        });
      } else {
        filtered = filtered.filter((item) => {
          return item.warehouse_stocks?.some(
            (ws) => ws.stock > ws.minimum_stock,
          );
        });
      }
    }

    return filtered;
  };

  const filteredItems = getFilteredItems();

  // Only apply pagination when not searching and not filtering
  const shouldPaginate =
    !searchTerm.trim() && statusFilter === "all" && !selectedCategory;
  const paginatedItems = shouldPaginate
    ? filteredItems.slice(
        (currentPage - 1) * itemsPerPage,
        (currentPage - 1) * itemsPerPage + itemsPerPage,
      )
    : filteredItems;

  // Get form items for current page
  const getCurrentPageFormItems = () => {
    return paginatedItems.map((invItem) => {
      const formItem = formData.items.find(
        (item) => item.inventory_id === invItem.id,
      );
      return (
        formItem || {
          inventory_id: invItem.id,
          quantity: 0,
          unit_price: 0,
          total_price: 0,
        }
      );
    });
  };

  const currentPageFormItems = getCurrentPageFormItems();

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const updateItem = (inventoryId, field, value) => {
    const updatedItems = formData.items.map((item) => {
      if (item.inventory_id === inventoryId) {
        let processedValue = value;

        // Handle number formatting for quantity and unit_price like MasterDataFormV2
        if (field === "quantity" || field === "unit_price") {
          if (typeof value === "string") {
            // If it's a string input, unformat it for storage
            processedValue = parseInt(unformatNumber(value)) || 0;
          } else {
            processedValue = value;
          }
        }

        const updatedItem = { ...item, [field]: processedValue };

        // Auto-calculate total_price when quantity or unit_price changes
        if (field === "quantity" || field === "unit_price") {
          updatedItem.total_price =
            (updatedItem.quantity || 0) * (updatedItem.unit_price || 0);
        }

        return updatedItem;
      }
      return item;
    });

    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  // Calculate totals
  useEffect(() => {
    const subtotal = formData.items.reduce(
      (sum, item) => sum + (item.total_price || 0),
      0,
    );
    const total =
      subtotal + (formData.tax_amount || 0) - (formData.discount_amount || 0);

    setFormData((prev) => ({
      ...prev,
      subtotal,
      total_amount: total,
    }));
  }, [formData.items, formData.tax_amount, formData.discount_amount]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.warehouse_id) {
      newErrors.warehouse_id = "Warehouse is required";
    }
    if (!formData.requester_id) {
      newErrors.requester_id = "Requester is required";
    }
    if (!formData.supplier_id) {
      newErrors.supplier_id = "Supplier is required";
    }

    // Check if at least one item has quantity > 0
    const hasValidItems = formData.items.some(
      (item) => item.quantity > 0 && item.unit_price >= 0,
    );
    if (!hasValidItems) {
      newErrors.items =
        "At least one item must have quantity > 0 and valid unit price";
    }

    // Validate items with quantity > 0
    formData.items.forEach((item, index) => {
      if (item.quantity > 0) {
        if (!item.inventory_id) {
          newErrors[`item_${index}_inventory`] = "Inventory item is required";
        }
        if (item.unit_price < 0) {
          newErrors[`item_${index}_unit_price`] =
            "Unit price cannot be negative";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError("");

    try {
      // Filter out items with zero quantity
      const filteredItems = formData.items.filter((item) => item.quantity > 0);

      const payload = {
        ...formData,
        items: filteredItems,
        // Remove calculated fields from payload
        subtotal: undefined,
        total_amount: undefined,
      };

      const url = isUpdateMode
        ? `${API_URL}/purchase-orders/${initialData.id}`
        : `${API_URL}/purchase-orders`;

      const response = await fetch(url, {
        method: isUpdateMode ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (onSuccess) {
        onSuccess(result);
      }

      if (onHide) {
        onHide();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const errorMessage = error.message || "Failed to save purchase order";
      setSubmitError(errorMessage);

      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form data when closing
    setFormData({
      warehouse_id: "",
      requester_id: "",
      supplier_id: "",
      note: "",
      items: [
        {
          inventory_id: "",
          quantity: 1,
          unit_price: 0,
          total_price: 0,
        },
      ],
      subtotal: 0,
      total_amount: 0,
    });
    setSelectedWarehouse(null);
    setSelectedRequester(null);
    setSelectedSupplier(null);
    // setSelectedItems([]);
    setErrors({});
    setSubmitError("");

    if (onHide) {
      onHide();
    }
  };

  return (
    <Card className="shadow-sm border-0">
      <Card.Header className=" border-bottom py-3 d-flex justify-content-between align-items-center">
        <h5 className="mb-0 fw-bold">
          {isUpdateMode ? "Edit" : "Create"} Purchase Order
        </h5>
        <Button variant="close" onClick={handleClose} aria-label="Close" />
      </Card.Header>
      <Form onSubmit={handleSubmit}>
        <Card.Body>
          {submitError && (
            <Alert variant="danger" className="mb-3">
              {submitError}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Warehouse *</Form.Label>
                <Select classNamePrefix="react-select" className="react-select-container"
                  options={warehouseOptions}
                  value={selectedWarehouse}
                  onChange={(selected) => {
                    setSelectedWarehouse(selected);
                    handleFormChange(
                      "warehouse_id",
                      selected ? selected.value : "",
                    );
                  }}
                  isSearchable
                  placeholder="Select warehouse..."
                  isClearable
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderColor: errors.warehouse_id ? "#dc3545" : "#dee2e6",
                      "&:hover": {
                        borderColor: errors.warehouse_id
                          ? "#dc3545"
                          : "#adb5bd",
                      },
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
                {errors.warehouse_id && (
                  <div className="text-danger small mt-1">
                    {errors.warehouse_id}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Supplier *</Form.Label>
                <Select classNamePrefix="react-select" className="react-select-container"
                  options={supplierOptions}
                  value={selectedSupplier}
                  onChange={(selected) => {
                    setSelectedSupplier(selected);
                    handleFormChange(
                      "supplier_id",
                      selected ? selected.value : "",
                    );
                  }}
                  isSearchable
                  placeholder="Select supplier..."
                  isClearable
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderColor: errors.supplier_id ? "#dc3545" : "#dee2e6",
                      "&:hover": {
                        borderColor: errors.supplier_id ? "#dc3545" : "#adb5bd",
                      },
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
                {errors.supplier_id && (
                  <div className="text-danger small mt-1">
                    {errors.supplier_id}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>note</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.note}
                  onChange={(e) => handleFormChange("note", e.target.value)}
                  placeholder="Enter note"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Advanced Filtering Section */}
          <Card className="mt-1 p-0">
            {/* <Card.Header>
              <h6 className="mb-0">Filter & Search Items</h6>
            </Card.Header> */}
            <Card.Body className="p-1">
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
                className="p-0"
              >
                <div style={{ flex: 1, minWidth: "250px" }}>
                  <InputGroup>
                    <InputGroup.Text>
                      <IconSearch size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search items by name, or description..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1); // Reset to first page when search changes
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                        }
                      }}
                    />
                  </InputGroup>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  {[
                    {
                      key: "all",
                      label: "All",
                      color: "#6b7280",
                      bgColor: "#f3f4f6",
                      icon: IconPackage,
                    },
                    {
                      key: "low",
                      label: "Low Stock",
                      color: "#d97706",
                      bgColor: "#fef3c7",
                      icon: IconAlertTriangle,
                    },
                    {
                      key: "out",
                      label: "Out of Stock",
                      color: "#dc2626",
                      bgColor: "#fee2e2",
                      icon: IconTrendingUp,
                    },
                  ].map((status) => {
                    const IconComponent = status.icon;
                    const isSelected = statusFilter === status.key;
                    return (
                      <Button
                        key={status.key}
                        variant={isSelected ? "primary" : "outline-secondary"}
                        size="sm"
                        onClick={() => {
                          setStatusFilter(status.key);
                          setCurrentPage(1); // Reset to first page when status changes
                        }}
                        className={`d-flex align-items-center gap-1 ${
                          isSelected ? "shadow-sm" : ""
                        }`}
                      >
                        <IconComponent size={16} />
                        {status.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Items Section */}
          <Card className="mt-3">
            <Card.Body>
              <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Item Name</th>
                      <th>Warehouse Stock</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Total Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageFormItems.map((item, index) => {
                      const inventoryItem = paginatedItems[index];

                      // Get warehouse stock data for selected warehouse
                      const warehouseStock =
                        inventoryItem?.warehouse_stocks?.find(
                          (ws) => ws.warehouse_id === formData.warehouse_id,
                        );

                      const isLowStock = warehouseStock
                        ? warehouseStock.stock <= warehouseStock.minimum_stock
                        : false;

                      return (
                        <tr key={item.inventory_id}>
                          <td style={{ minWidth: "150px" }}>
                            <strong>
                              {inventoryItem?.name || "Unknown Item"}
                            </strong>
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1">
                              <span
                                className={`badge text-white ${
                                  isLowStock ? "bg-danger" : "bg-success"
                                }`}
                              >
                                Stock: {warehouseStock?.stock || 0}
                              </span>
                              <small className="text-muted">
                                Min: {warehouseStock?.minimum_stock || 0}
                              </small>
                              {warehouseStock?.status && (
                                <small
                                  className={`text-${
                                    warehouseStock.status === "Stok Rendah"
                                      ? "danger"
                                      : "success"
                                  }`}
                                >
                                  {warehouseStock.status}
                                </small>
                              )}
                            </div>
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              value={formatNumber(String(item.quantity || 0))}
                              onChange={(e) =>
                                updateItem(
                                  item.inventory_id,
                                  "quantity",
                                  e.target.value,
                                )
                              }
                              style={{ width: "80px" }}
                              placeholder="0"
                            />
                          </td>
                          <td>
                            <div className="d-flex flex-column">
                              <div className="d-flex align-items-center">
                                <Form.Control
                                  type="text"
                                  value={formatNumber(
                                    String(Math.floor(item.unit_price || 0)),
                                  )}
                                  onChange={(e) =>
                                    updateItem(
                                      item.inventory_id,
                                      "unit_price",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="0"
                                />
                              </div>
                              <small className="text-muted mt-1">
                                Master Price: Rp{" "}
                                {formatRupiah(Math.floor(inventoryItem?.price || 0))}
                              </small>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              {/* <Form.Control
                                type="number"
                                value={Math.floor(item.total_price)}
                                readOnly
                                // style={{ width: "100px" }}
                              /> */}
                              <span className="ms-2 text-muted">
                                ({formatRupiah(Math.floor(item.total_price))})
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Pagination - only show when paginating and items exceed page size */}
              {Pagination &&
                shouldPaginate &&
                filteredItems.length > itemsPerPage && (
                  <div
                    style={{ padding: "10px", borderTop: "1px solid #e5e7eb" }}
                  >
                    {Pagination}
                  </div>
                )}
            </Card.Body>
          </Card>
        </Card.Body>
        <Card.Footer className=" border-top py-3">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div>
              <strong>Subtotal: Rp {formatRupiah(formData.subtotal)}</strong>
            </div>
            <div>
              <Button
                variant="secondary"
                onClick={handleClose}
                className="me-2"
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading && <Spinner size="sm" className="me-2" />}
                {isUpdateMode ? "Update" : "Create"} Purchase Order
              </Button>
            </div>
          </div>
        </Card.Footer>
      </Form>
      {/* <PrintJson data={initialData} title="Purchase Order Form Data" /> */}
      {/* <PrintJson data={inventory} title="inventory" /> */}
    </Card>
  );
};

export default PurchaseOrderForm;
